import { Props_SnippetStandardAccordion } from "./StandardAccordion.props";
import SnippetLink from '../Shared/Link/Link';
import { useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { CompileHeadingToSpan } from '../../../../Shared/AttraxTs/React/ReactSnippetMDCompiler'

const ANIMATION_DURATION = 0.3;

type StandardAccordionRowProps = {
    accordionRow: Props_SnippetStandardAccordion['repeatable_accordionRow'][number];
    index: number;
    isOpen: boolean;
    isDetailsOpen: boolean;
    onToggle: (index: number, event: React.MouseEvent) => void;
    onExitComplete: (index: number) => void;
};

const StandardAccordionRow = ({ accordionRow, index, isOpen, isDetailsOpen, onToggle, onExitComplete }: StandardAccordionRowProps) => {
    const { linkText, linkHref, linkStyle, linkTarget, title, text } = accordionRow;

    return (
        <details
            key={"standard-accordion-row-" + index}
            className={`snippet-standard-accordion__row ${isOpen ? "snippet-standard-accordion__row--open" : ""}`}
            open={isDetailsOpen}
        >
            <summary
                className="snippet-standard-accordion__row-title"
                onClick={(e) => onToggle(index, e)}
            >
                {title?.value ? title.value : "Accordion Row " + (index + 1)}
            </summary>
            <AnimatePresence initial={false} onExitComplete={() => onExitComplete(index)}>
                {isOpen && (
                    <motion.div
                        className="snippet-standard-accordion__content-container"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: ANIMATION_DURATION, ease: "easeInOut" }}
                    >
                        {text && (
                            <p className="snippet-standard-accordion__text">{text.value}</p>
                        )}
                        {linkHref?.value && linkText?.value && (
                            <SnippetLink
                                linkStyles={linkStyle?.value}
                                className="snippet-standard-accordion__link"
                                url={linkHref?.value}
                                openInNewTab={!!linkTarget?.value}
                                rel={linkTarget ? "noopener noreferrer" : undefined}
                            >
                                {linkText?.value}
                            </SnippetLink>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </details>
    );
};

const StandardAccordion = (props: Props_SnippetStandardAccordion) => {
    const { accordionTitle, repeatable_accordionRow } = props;
    const [openIndex, setOpenIndex] = useState<number | null>(null);
    const [detailsOpenSet, setDetailsOpenSet] = useState<Set<number>>(new Set());
    const accordionTitleClass = accordionTitle.visibility?.value ? "" : "accessible-heading";

    const handleToggle = (index: number, event: React.MouseEvent) => {
        event.preventDefault();
        
        if (openIndex === index) {
            setOpenIndex(null);
        } else {
            setOpenIndex(index);
            setDetailsOpenSet(prev => new Set(prev).add(index));
        }
    };

    const handleExitComplete = (index: number) => {
        setDetailsOpenSet(prev => {
            const newSet = new Set(prev);
            newSet.delete(index);
            return newSet;
        });
    };

    return (
        <section className="snippet-standard-accordion">
            <div className="snippet-standard-accordion__container">
                {accordionTitle?.text?.value && (
                    <h2 className={`snippet-standard-accordion__title ${accordionTitleClass}`}>
                        {CompileHeadingToSpan(accordionTitle.text.value)}
                    </h2>
                )}

                <div className="snippet-standard-accordion__row-container">
                    {repeatable_accordionRow.map((accordionRow, index) => (
                        <StandardAccordionRow
                            key={"standard-accordion-row-" + index}
                            accordionRow={accordionRow}
                            index={index}
                            isOpen={openIndex === index}
                            isDetailsOpen={detailsOpenSet.has(index)}
                            onToggle={handleToggle}
                            onExitComplete={handleExitComplete}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default StandardAccordion;
